/**
 * Video source detection utility
 * Determines the source type of a video URL
 */

export type VideoSource = 'youtube' | 'supabase_storage' | 'cloudflare_r2' | 'local' | 'unknown';

/**
 * Detects the video source type from a video URL
 * @param videoUrl - The video URL to analyze
 * @returns The detected video source type
 */
export function detectVideoSource(videoUrl: string): VideoSource {
  if (!videoUrl || typeof videoUrl !== 'string') {
    return 'unknown';
  }

  // YouTube detection
  if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
    return 'youtube';
  }

  // Supabase Storage detection (supports custom domains)
  if (
    videoUrl.includes('supabase.co/storage') ||
    videoUrl.includes('supabase.storage') ||
    videoUrl.includes('/storage/v1/')
  ) {
    return 'supabase_storage';
  }

  // Cloudflare R2 / CDN detection
  if (
    videoUrl.includes('r2.cloudflarestorage.com') ||
    videoUrl.includes('.r2.cloudflare') ||
    videoUrl.includes('cdn.capture.kridah.com')
  ) {
    return 'cloudflare_r2';
  }

  // Local storage detection (blob URLs or file://)
  if (videoUrl.startsWith('blob:') || videoUrl.startsWith('file://')) {
    return 'local';
  }

  return 'unknown';
}

/**
 * Extracts YouTube video ID from various YouTube URL formats
 * @param videoUrl - YouTube URL in any format
 * @returns YouTube video ID or null if not found
 */
export function extractYouTubeVideoId(videoUrl: string): string | null {
  if (!videoUrl || typeof videoUrl !== 'string') {
    console.log('%c[utils] extractYouTubeVideoId: Invalid URL', 'color: red; font-weight: bold;', { videoUrl });
    return null;
  }

  // Match various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^"&?\/\s]{11})/,
    /youtube\.com\/watch\?.*v=([^"&?\/\s]{11})/,
  ];

  for (const pattern of patterns) {
    const match = videoUrl.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * Converts YouTube URL to embed URL format
 * @param videoUrl - YouTube URL in any format
 * @returns YouTube embed URL
 */
export function getYouTubeEmbedUrl(videoUrl: string): string | null {
  const videoId = extractYouTubeVideoId(videoUrl);
  if (!videoId) {
    return null;
  }
  return `https://www.youtube.com/embed/${videoId}`;
}

